import { Card, CardActionArea, CardContent, IconButton, Skeleton, Tooltip } from "@mui/material";
import { ComponentType, ReactNode } from "react";
import { MdChevronRight, MdRefresh } from "react-icons/md";
import { Link } from "react-router-dom";

export type KpiAccent = "blue" | "violet" | "teal" | "amber" | "sky" | "pink" | "green" | "red";

interface KpiCardProps {
  icon: ComponentType<{ size?: number | string }>;
  label: ReactNode;
  value: ReactNode;
  /** Подсказка при наведении на название: что именно считается. */
  hint?: string;
  accent?: KpiAccent;
  /** Ссылка на раздел с подробностями — вся карточка становится кликабельной. */
  to?: string;
  loading?: boolean;
  /** Идёт фоновое обновление (например, при смене периода) — показываем прошлое значение приглушённо. */
  fetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

/** Единая карточка-счётчик для дашборда. */
const KpiCard = ({ icon: Icon, label, value, hint, accent = "blue", to, loading, fetching, error, onRetry }: KpiCardProps) => {
  const hasError = !!error && !loading;

  const content = (
    <CardContent className="kpi-card__content">
      <div className={`kpi-card__icon kpi-card__icon--${accent}`}>
        <Icon size={22} />
      </div>
      <div className="kpi-card__body">
        <Tooltip title={hint || ""} placement="top-start" disableInteractive>
          <div className="kpi-card__label">{label}</div>
        </Tooltip>
        {loading ? (
          <Skeleton variant="text" width={90} height={32} />
        ) : hasError ? (
          <div className="kpi-card__error">
            Не загрузилось
            {onRetry && (
              <Tooltip title="Повторить">
                <IconButton size="small" onClick={onRetry} aria-label="Повторить загрузку">
                  <MdRefresh size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ) : (
          <div className={`kpi-card__value${fetching ? " is-fetching" : ""}`}>{value}</div>
        )}
      </div>
      {to && !hasError && <MdChevronRight className="kpi-card__arrow" size={20} />}
    </CardContent>
  );

  return (
    <Card className="kpi-card">
      {/* Ссылку не ставим в состоянии ошибки, чтобы клик по «Повторить» не уводил со страницы */}
      {to && !hasError ? (
        <CardActionArea component={Link} to={to} className="kpi-card__action">
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
};

export default KpiCard;
